import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "./Components/BrandLogo";
import ProfileMenu from "./Components/ProfileMenu";
import UserContext from "common/Context/UserContext";
import Clock from "./Components/Clock";
import "./Header.styles.css";
import { useHeaderComponentHooks } from "./Header.hooks";
import CustomAlert from "../CustomAlert";
import SelectedCustomerDropdown from "../SelectedCustomerDropDown/SelectedCustomerDropDown";
import {
  shouldShowCustomerDropdown,
  isPlatformAdminProfile,
} from "common/Utilities/Role.utils";
import { resetPlatformAdminCustomerSelection } from "common/Utilities/CustomerSelection.utils";
import {
  StyledRightSection,
  StyledCustomerName,
  StyledLanguageIcon,
  StyledProfileIconButton,
  StyledAvatarWrapper,
  StyledAvatar,
} from "./Header.style";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import Tooltip from "@mui/material/Tooltip";

const reset = 1;
const success = 2;
const fail = 3;

const Header = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getCustomerTimeZone, getCustomerName, userProfile, setHeaderSelectedCustomer } =
    useContext(UserContext);
  const timezone = getCustomerTimeZone() ?? "";
  const customerName = getCustomerName() ?? "";
  const userId = userProfile?.profile?.userid ?? "0";
  const { state, actions } = useHeaderComponentHooks();
  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertInfo, setAlertInfo] = useState({});
  const [anchorElUser, setAnchorElUser] = useState(null);
  const profile = userProfile?.profile;
  const cognitoUsername =
    profile?.userid != null && String(profile.userid).trim() !== ""
      ? String(profile.userid).trim() : "";
  const { logout } = useContext(UserContext);
  const showCustomerDropDown = shouldShowCustomerDropdown(profile);
  const showClockAndName = userId !== "0" && !!customerName?.trim();
  const showHomeButton = isPlatformAdminProfile(profile) && pathname !== "/";

  const handleHomeClick = () => {
    resetPlatformAdminCustomerSelection();
    setHeaderSelectedCustomer(null);
    if (isPlatformAdminProfile(profile)) {
      window.location.assign("/");
      return;
    }
    navigate("/");
  };
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = (title) => {
    switch (title) {
      case "Logout":
        logout();
        break;
      case "Administration":
        navigate(INFRAMARK_CONSTANTS?.navigationRoutes?.administration);
        break;
      case "Reset Password":
        sendResetPasswordLink();
        break;
      default:
        break;
    }
    setAnchorElUser(null);
  };
  const sendResetPasswordLink = () => {
    setAlertInfo({
      title: "Reset Password",
      message: `Are you sure you want to reset your password?\nA code will be sent to your registered email address.`,
      type: reset,
    });
    setShowCustomAlert(true);
  };

  useEffect(() => {
    if (!state.loading && state.error) {
      setAlertInfo({
        title: "Reset Password",
        message: state.error,
        type: fail,
      });
      setShowCustomAlert(true);
    } else if (!state.loading && state.success) {
      setAlertInfo({
        title: "Reset Password",
        message:
          "A code has been sent to your registered email. You will be taken to a page to enter the code and your new password.",
        type: success,
      });
      setShowCustomAlert(true);
    }
  }, [state]);

  const alarmCallBack = (type) => {
    setAlertInfo({});
    setShowCustomAlert(false);
    switch (type) {
      case reset:
        actions.resetPassword(cognitoUsername);
        break;
      case success:
        actions.reset();
        navigate("/confirm-reset-password", {
          state: {
            username: cognitoUsername,
            email: profile?.email,
          },
        });
        break;
      case fail:
        actions.reset();
        break;
      default:
        break;
    }
  };

  return (
    <div className="header-top header-bg navbar navbar-expand-lg navbar-light d-flex justify-content-between align-items-center flex-nowrap">
      <BrandLogo />
      <StyledRightSection>
        {!showCustomerDropDown && (
          <div>
            {showClockAndName && timezone && <Clock timezone={timezone} />}
            <StyledCustomerName>{customerName}</StyledCustomerName>
          </div>
        )}
        {showCustomerDropDown && <SelectedCustomerDropdown />}
        {showHomeButton && (
          <StyledLanguageIcon
            onClick={handleHomeClick}
            alt="Globe Icon"
            aria-label="Go to home"
          />
        )}
        <Tooltip title="Profile">
          <StyledProfileIconButton onClick={handleOpenUserMenu}>
            <StyledAvatarWrapper>
              {!anchorElUser && (
                <StyledAvatar alt="Profile Icon" src="" />
              )}
            </StyledAvatarWrapper>
          </StyledProfileIconButton>
        </Tooltip>
        <ProfileMenu anchorElUser={anchorElUser} handleCloseUserMenu={handleCloseUserMenu} userProfile={userProfile} />
      </StyledRightSection>
      {showCustomAlert && (
        <CustomAlert
          show={showCustomAlert}
          title={alertInfo.title}
          message={alertInfo.message}
          onOkay={() => {
            alarmCallBack(alertInfo.type);
          }}
          onCancel={
            alertInfo.type === reset
              ? () => {
                alarmCallBack(null);
              }
              : null
          }
        />
      )}
    </div>
  );
};

export default Header;
